class HomeTabbedCollection extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.enableLoop = this.dataset.loop === 'true';
    this.enableAutoplay = this.dataset.autoplay === 'true';
    this.autoplayDelay = (Number(this.dataset.autoplayDelay) || 5) * 1000;
    this.sliderSpeed = Number(this.dataset.sliderSpeed) || 500;
    this.updateHeading = this.dataset.updateHeading === 'true';
    this.enableAjax = this.dataset.ajaxTabs === 'true';
    this.swipers = new Map();
    this.activeTabId = null;
    this.isTabTransitioning = false;
    this.panelTransitionMs = 400;
    this._onResize = this.debounce(() => this.onResize(), 200);
  }

  debounce(fn, delay) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), delay);
    };
  }

  connectedCallback() {
    this.headingEl = this.querySelector('[data-htc-heading]');
    this.panelsWrap = this.querySelector('[data-panels]');
    this.tabButtons = this.querySelectorAll('[data-tab-button]');

    this.tabButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => this.onTabClick(e));
    });

    const active = this.querySelector('[data-tab-button].is-active');
    if (active) {
      this.activeTabId = active.dataset.tabId;
      if (this.updateHeading) {
        this.syncHeading(active.dataset.tabLabel);
      }
    }

    window.addEventListener('resize', this._onResize);

    this.addEventListener('shopify:section:load', () => {
      this.destroyAllSwipers();
      this.initAllPanels();
    });

    this.querySelectorAll('.htc__slider-wrap').forEach((wrap) => {
      this.getBleedMetrics(wrap);
    });

    requestAnimationFrame(() => this.initAllPanels());
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    this.destroyAllSwipers();
  }

  syncHeading(label) {
    if (this.headingEl && label) {
      this.headingEl.textContent = label;
    }
  }

  onTabClick(event) {
    const tabId = event.currentTarget.dataset.tabId;
    if (!tabId || tabId === this.activeTabId || this.isTabTransitioning) return;
    this.activateTab(tabId, true);
  }

  wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  getPanelTransitionMs() {
    return this.prefersReducedMotion() ? 0 : this.panelTransitionMs;
  }

  setTabButtonsActive(tabId) {
    this.tabButtons.forEach((btn) => {
      const active = btn.dataset.tabId === tabId;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  async syncHeadingWithFade(label) {
    if (!this.headingEl || !label) return;

    const duration = this.getPanelTransitionMs();
    if (!duration) {
      this.syncHeading(label);
      return;
    }

    this.headingEl.classList.add('is-fading');
    await this.wait(Math.min(duration * 0.45, 180));
    this.syncHeading(label);
    await this.wait(20);
    this.headingEl.classList.remove('is-fading');
  }

  async activateTab(tabId, announce) {
    const button = this.querySelector(`[data-tab-button][data-tab-id="${tabId}"]`);
    if (!button) return;

    const outgoingPanel = this.querySelector('[data-panel].is-active');
    let incomingPanel = this.querySelector(`[data-panel][data-tab-id="${tabId}"]`);
    if (!incomingPanel) return;

    if (incomingPanel === outgoingPanel) return;

    this.isTabTransitioning = true;
    this.setTabButtonsActive(tabId);

    if (incomingPanel.hasAttribute('data-lazy-panel') && this.enableAjax) {
      incomingPanel = await this.loadPanel(tabId, incomingPanel);
    }

    const transitionMs = this.getPanelTransitionMs();

    if (this.panelsWrap && transitionMs) {
      this.panelsWrap.classList.add('is-transitioning');
      if (outgoingPanel) {
        this.panelsWrap.style.minHeight = `${outgoingPanel.offsetHeight}px`;
      }
    }

    if (outgoingPanel) {
      outgoingPanel.classList.remove('is-active');
      if (transitionMs) {
        outgoingPanel.classList.add('is-exiting');
      } else {
        outgoingPanel.setAttribute('aria-hidden', 'true');
        this.destroySwiper(outgoingPanel.dataset.tabId);
      }
    }

    incomingPanel.classList.remove('is-exiting');
    incomingPanel.setAttribute('aria-hidden', 'false');
    incomingPanel.classList.add('is-active');

    if (transitionMs) {
      incomingPanel.classList.add('is-entering');
    }

    if (this.updateHeading) {
      if (transitionMs) {
        this.syncHeadingWithFade(button.dataset.tabLabel);
      } else {
        this.syncHeading(button.dataset.tabLabel);
      }
    }

    if (transitionMs) {
      await this.wait(20);
      incomingPanel.classList.remove('is-entering');
      await this.wait(transitionMs);

      if (outgoingPanel) {
        outgoingPanel.classList.remove('is-exiting');
        outgoingPanel.setAttribute('aria-hidden', 'true');
        this.destroySwiper(outgoingPanel.dataset.tabId);
      }
    } else if (outgoingPanel) {
      outgoingPanel.setAttribute('aria-hidden', 'true');
      this.destroySwiper(outgoingPanel.dataset.tabId);
    }

    this.querySelectorAll('[data-panel]').forEach((panel) => {
      if (panel.dataset.tabId === tabId) return;
      if (panel === outgoingPanel && panel.classList.contains('is-exiting')) return;

      panel.classList.remove('is-active', 'is-entering', 'is-exiting');
      panel.setAttribute('aria-hidden', 'true');
      this.destroySwiper(panel.dataset.tabId);
    });

    if (this.panelsWrap) {
      this.panelsWrap.classList.remove('is-transitioning');
      this.panelsWrap.style.removeProperty('min-height');
    }

    this.activeTabId = tabId;
    this.isTabTransitioning = false;

    requestAnimationFrame(() => {
      this.initPanelSwiper(incomingPanel);
      this.refreshProductLabels();
    });

    if (announce) {
      button.focus();
    }
  }

  async loadPanel(tabId, placeholder) {
    const url = `${window.location.pathname}?section_id=${this.sectionId}&tab_block_id=${encodeURIComponent(tabId)}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Tab load failed');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root =
        doc.querySelector(`#shopify-section-${this.sectionId}`) ||
        doc.querySelector(`[id*="shopify-section"]`);
      const panel = root?.querySelector(`[data-panel][data-tab-id="${tabId}"]`);
      if (panel) {
        panel.classList.add('is-active');
        panel.setAttribute('aria-hidden', 'false');
        panel.removeAttribute('data-lazy-panel');
        placeholder.replaceWith(panel);
        return panel;
      }
    } catch (err) {
      console.warn('Home tabbed collection:', err);
      placeholder.innerHTML = '<p class="htc__empty">Unable to load products.</p>';
      placeholder.classList.add('is-active');
    }
    return placeholder;
  }

  initAllPanels() {
    this.querySelectorAll('[data-panel]').forEach((panel) => {
      if (panel.hasAttribute('data-lazy-panel')) return;
      if (panel.classList.contains('is-active')) {
        this.initPanelSwiper(panel);
      }
    });
  }

  onResize() {
    const panel = this.querySelector('[data-panel].is-active');
    if (panel) {
      const tabId = panel.dataset.tabId;
      this.destroySwiper(tabId);
      this.initPanelSwiper(panel);
    }
  }

  getBleedMetrics(wrap) {
    const inner = this.querySelector('.home-tabbed-collection__inner');
    if (!inner || !wrap) {
      return { bleedLeft: 0, bleedRight: 0, contentWidth: wrap?.clientWidth || 0 };
    }

    const innerRect = inner.getBoundingClientRect();
    const styles = window.getComputedStyle(inner);
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const padRight = parseFloat(styles.paddingRight) || 0;
    const contentWidth = Math.max(0, inner.clientWidth - padLeft - padRight);
    const contentLeft = innerRect.left + padLeft;
    const contentRight = contentLeft + contentWidth;
    const viewportWidth = document.documentElement.clientWidth;
    const bleedLeft = Math.max(0, contentLeft);
    const bleedRight = Math.max(0, viewportWidth - contentRight);

    wrap.style.setProperty('--htc-bleed-left', `${bleedLeft}px`);
    wrap.style.setProperty('--htc-bleed-right', `${bleedRight}px`);
    wrap.style.setProperty('--htc-content-width', `${contentWidth}px`);

    return { bleedLeft, bleedRight, contentWidth };
  }

  getSpaceBetween() {
    const width = window.innerWidth;
    if (width >= 990) return 19;
    if (width >= 750) return 20;
    return 16;
  }

  getSlidesPerViewSetting(swiperEl) {
    const width = window.innerWidth;
    const mobile = Number(swiperEl.dataset.slidesMobile) || 1.2;
    const tablet = Number(swiperEl.dataset.slidesTablet) || 2;
    const desktop = Number(swiperEl.dataset.slidesDesktop) || 4;
    if (width >= 990) return desktop;
    if (width >= 750) return tablet;
    return mobile;
  }

  getTrackSlidesPerView(contentWidth, bleedLeft, bleedRight, perView, spaceBetween) {
    if (!contentWidth || !perView) return perView;
    const slideWidth = (contentWidth - (perView - 1) * spaceBetween) / perView;
    if (slideWidth <= 0) return perView;
    const trackWidth = contentWidth + bleedLeft + bleedRight;
    return (trackWidth + spaceBetween) / (slideWidth + spaceBetween);
  }

  applyBleedLayout(wrap, swiperEl) {
    const { bleedLeft, bleedRight, contentWidth } = this.getBleedMetrics(wrap);
    const perView = this.getSlidesPerViewSetting(swiperEl);
    const spaceBetween = this.getSpaceBetween();
    const slidesPerView = this.getTrackSlidesPerView(
      contentWidth,
      bleedLeft,
      bleedRight,
      perView,
      spaceBetween
    );

    const slidesOffsetBefore = this.enableLoop ? 0 : bleedLeft;
    const slidesOffsetAfter = this.enableLoop ? 0 : bleedRight;

    return {
      bleedLeft,
      bleedRight,
      contentWidth,
      spaceBetween,
      slidesPerView,
      slidesOffsetBefore,
      slidesOffsetAfter,
    };
  }

  getSwiperOptions(swiperEl, wrap) {
    const nextEl = wrap.querySelector('[data-htc-next]');
    const prevEl = wrap.querySelector('[data-htc-prev]');
    const {
      spaceBetween,
      slidesPerView,
      slidesOffsetBefore,
      slidesOffsetAfter,
    } = this.applyBleedLayout(wrap, swiperEl);

    const options = {
      speed: this.sliderSpeed,
      loop: this.enableLoop,
      slidesPerView,
      spaceBetween,
      slidesOffsetBefore,
      slidesOffsetAfter,
      grabCursor: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
    };

    if (nextEl && prevEl) {
      options.navigation = {
        nextEl,
        prevEl,
        disabledClass: 'swiper-button-disabled',
        lockClass: 'swiper-button-lock',
      };
    }

    if (this.enableAutoplay) {
      options.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    return options;
  }

  // Centre the arrows on the card image rather than the whole card, so they
  // don't drift down as titles/prices wrap to different heights.
  positionNav(panel) {
    const nav = panel.querySelector('[data-htc-nav]');
    if (!nav) return;

    const media = panel.querySelector('.htc__slide .card__inner, .htc__slide .card__media');
    const mediaHeight = media ? media.getBoundingClientRect().height : 0;
    if (mediaHeight) {
      nav.style.setProperty('--htc-nav-top', `${mediaHeight / 2}px`);
    }

    // Reveal only now — before this the arrows would sit at the 50% fallback.
    nav.setAttribute('data-htc-nav-ready', '');
  }

  initPanelSwiper(panel) {
    const tabId = panel.dataset.tabId;
    const swiperEl = panel.querySelector('[data-htc-swiper]');
    const wrap = panel.querySelector('.htc__slider-wrap');
    if (!swiperEl || !wrap || typeof Swiper === 'undefined') return;

    const syncBleedParams = (swiper) => {
      const layout = this.applyBleedLayout(wrap, swiperEl);
      swiper.params.slidesPerView = layout.slidesPerView;
      swiper.params.spaceBetween = layout.spaceBetween;
      swiper.params.slidesOffsetBefore = layout.slidesOffsetBefore;
      swiper.params.slidesOffsetAfter = layout.slidesOffsetAfter;
      swiper.update();
    };

    const existing = this.swipers.get(tabId);
    if (existing && !existing.destroyed) {
      syncBleedParams(existing);
      this.bindProgress(panel, existing);
      this.positionNav(panel);
      swiperEl.classList.add('htc-swiper--initialized');
      return;
    }

    this.applyBleedLayout(wrap, swiperEl);
    void wrap.offsetWidth;

    const swiper = new Swiper(swiperEl, this.getSwiperOptions(swiperEl, wrap));
    this.swipers.set(tabId, swiper);
    this.bindProgress(panel, swiper);
    swiperEl.classList.add('htc-swiper--initialized');
    this.positionNav(panel);
    swiper.on('imagesReady', () => this.positionNav(panel));
    swiper.on('resize', () => {
      syncBleedParams(swiper);
      this.positionNav(panel);
    });
  }

  bindProgress(panel, swiper) {
    const progress = panel.querySelector('[data-htc-progress]');
    const fill = panel.querySelector('.htc__progress-fill');
    if (!progress || !fill) return;

    if (swiper._htcProgressUpdate) {
      swiper.off('slideChange', swiper._htcProgressUpdate);
      swiper.off('progress', swiper._htcProgressUpdate);
      swiper.off('resize', swiper._htcProgressUpdate);
    }

    const update = () => {
      if (!swiper.slides.length) return;
      const total = swiper.slides.length;

      if (!swiper.params.loop) {
        const ratio = typeof swiper.progress === 'number' ? swiper.progress : 0;
        const minPct = Math.min((3 / Math.max(total, 3)) * 100, 40);
        if (ratio <= 0.001) {
          fill.style.width = `${Math.max(minPct, 8)}%`;
        } else if (swiper.isEnd || ratio >= 0.999) {
          fill.style.width = '100%';
        } else {
          fill.style.width = `${Math.min(Math.max(ratio * 100, minPct), 100)}%`;
        }
        fill.style.transform = 'translateX(0)';
        return;
      }

      const perView =
        typeof swiper.params.slidesPerView === 'number'
          ? swiper.params.slidesPerView
          : swiper.slidesPerViewDynamic?.() || 1;
      const maxIndex = Math.max(total - perView, 1);
      const ratio = Math.min(swiper.activeIndex / maxIndex, 1);
      const widthPct = Math.min((perView / total) * 100, 100);
      fill.style.width = `${widthPct}%`;
      fill.style.transform = `translateX(${ratio * (100 - widthPct)}%)`;
    };

    swiper._htcProgressUpdate = update;
    swiper.on('slideChange', update);
    swiper.on('progress', update);
    swiper.on('resize', update);
    update();
  }

  destroySwiper(tabId) {
    const swiper = this.swipers.get(tabId);
    if (swiper) {
      const swiperEl = swiper.el;
      swiper.destroy(true, true);
      this.swipers.delete(tabId);
      if (swiperEl) {
        swiperEl.classList.remove('htc-swiper--initialized');
      }
    }
  }

  destroyAllSwipers() {
    this.swipers.forEach((s) => s.destroy(true, true));
    this.swipers.clear();
  }

  refreshProductLabels() {
    if (window.DLB?.refresh) window.DLB.refresh();
    if (window.DecoLabelsBadges?.refresh) window.DecoLabelsBadges.refresh();
    document.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: this.sectionId },
      })
    );
  }
}

if (!customElements.get('home-tabbed-collection')) {
  customElements.define('home-tabbed-collection', HomeTabbedCollection);
}
